import { FC, useContext } from "react";
import {
  AppContext,
  InlineMultipleRowsProps,
  View,
  useAppContext,
  useClientContext,
  useInlineContext,
  useNavigation,
  useHeaders,
  withGroups,
  ActionBarList,
} from "@data-root/react";
import {
  Avatar,
  Button,
  Empty,
  Skeleton,
  Space,
  Tag,
  List as _List,
} from "antd";
import {
  FormulaContext,
  ListView,
  RowWithEmbeddedMetadata,
} from "@data-root/types";
import { useAsyncCallback } from "@data-root/react";
import { useContextMenu } from "react-contexify";
import InfiniteScroll from "react-infinite-scroll-component";

interface ListItemProps {
  getContext: () => FormulaContext;
  row: RowWithEmbeddedMetadata;
}

export const ListItem: FC<ListItemProps> = ({ getContext, row }) => {
  const { core } = useClientContext();
  const { appSpecifier } = useAppContext();
  const { view, onClick: _onClick } = useInlineContext();
  const { navigation } = useNavigation();

  const { id: rowId } = row;
  const { options } = view as ListView;

  const { primaryHeader, secondaryHeader, imageUri } = useHeaders(
    // appSpecifier,
    // view.tableId,
    getContext,
    row,
    options.primaryHeader,
    options.secondaryHeader,
    options.image
  );

  // const { attributes, listeners, setNodeRef } = useDraggable({
  //   id: row.id,
  //   data: {
  //     type: "row",
  //     rowId: row.id,
  //   },
  //   disabled: !desktop,
  // });

  const { show } = useContextMenu({
    id: row.id,
  });

  const onClick = useAsyncCallback(async () => {
    if (_onClick) return _onClick(row);
    if (!options.onClick) return;

    const onClick = await core.evaluateFormula<string>(options.onClick, {
      appId: appSpecifier.appId,
      deploymentId: appSpecifier.deploymentId,
      row,
    });
    navigation.navigate(onClick, { rowId: row.id });
  }, [
    _onClick,
    row,
    options.onClick,
    core,
    appSpecifier.appId,
    appSpecifier.deploymentId,
    navigation,
  ]);

  return (
    <_List.Item
      // ref={setNodeRef}
      actions={
        options.actionIds
          ? ActionBarList({
              getContext,
              actionIds: options.actionIds,
            })
          : undefined
      }
    >
      <Skeleton avatar title={false} loading={false} active>
        <_List.Item.Meta
          avatar={imageUri ? <Avatar src={imageUri} /> : undefined}
          title={
            <Space>
              <div
                style={{ userSelect: "none" }}
                onClick={onClick}
                onContextMenu={(event) => show({ event })}
                // {...listeners}
                // {...attributes}
              >
                {primaryHeader}
              </div>

              {!row.$metadata.online && (
                <Tag bordered={false} color="error">
                  Offline
                </Tag>
              )}
            </Space>
          }
          description={secondaryHeader}
        />
      </Skeleton>
    </_List.Item>
  );
};

export const ListItemSkeleton = () => {
  return (
    <_List.Item>
      <Skeleton avatar loading active>
        <_List.Item.Meta avatar={null} title="" description="" />
      </Skeleton>
    </_List.Item>
  );
};

export const ListSkeleton = () => {
  return (
    <_List
      dataSource={[1, 2, 3, 4, 5]}
      itemLayout="horizontal"
      renderItem={() => <ListItemSkeleton />}
    />
  );
};

export const List: FC<InlineMultipleRowsProps> = withGroups(
  ({ containerId, getContext, rows, totalRows, onFetch, loaded, fetching }) => {
    const { styles } = useContext(AppContext)!;

    if (!loaded) return <ListSkeleton />;

    if (rows.length === 0)
      return (
        <View style={{ height: "100%" }}>
          <Empty style={{ margin: "auto" }} />
        </View>
      );

    return (
      <InfiniteScroll
        dataLength={rows.length}
        next={onFetch}
        hasMore={rows.length < totalRows}
        loader={
          <Skeleton avatar loading active>
            <_List.Item.Meta avatar={null} title="" description="" />
          </Skeleton>
        }
        scrollableTarget={containerId}
      >
        <_List
          dataSource={rows}
          itemLayout="horizontal"
          renderItem={(item) => {
            const getExtendedContext = () => ({
              ...getContext(),
              row: item,
              rowId: item.id,
            });

            return <ListItem getContext={getExtendedContext} row={item} />;
          }}
          footer={
            rows.length < totalRows && onFetch ? (
              <Button
                type="link"
                onClick={onFetch}
                style={{
                  color: styles.primaryColor,
                }}
                loading={fetching}
              >
                Cargar más
              </Button>
            ) : undefined
          }
        />
      </InfiniteScroll>
    );
  }
);
